define("Inputs/Dropdown", [`Input`, `module`], (Super, Self) => {
	return Super.inherit({
		namespace: Self.id,
		requires: [`Locale`, `XHR`],
		defaults: {
			hideValueOnOpen: true, // Whether or not the value should disappear when focusing on the input
			isIconAfterValue: false,
			listItemHasRadioButton: false, // Currently only used by FilterSearchBar.js
		},
		constructor(setup) {
			const { DOM, Locale, data, elements, instances } = this;
			const { rules, validation } = data;
			const { listItemHasRadioButton, hideValueOnOpen, isIconAfterValue } = setup;
			const done = true;
			let listContainer;
			let resetButton;
			let arrowIcon;
			let promise;

			this.defaults(validation, { required: true });
			this.assign(data, {
				open: false,
				query: ``,
				suggestedIndex: null,
				listItemHasRadioButton,
				hideValueOnOpen,
				isIconAfterValue,
			});

			this.render();
			this.addClass(`has-right-icon`); // for down-arrow / reset button spacing

			arrowIcon = this.get(`.icon-arrow-down`);
			listContainer = this.get(`ul`);
			resetButton = this.get(`[action="reset"]`);
			this.assign(elements, { arrowIcon, listContainer, resetButton });

			elements.options = []; // used for caching the corresponding li per option

			this.hide({ done, target: listContainer });

			if (validation.required) {
				this.hide({ done, target: resetButton });
			}

			Locale.get({
				fireImmediately: true,
				subscribe: ({ selected }) => {
					data.useTranslatedLabel = selected.key !== `en_US`;
				},
			});

			promise = this.create({
				namespace: `Inputs/Textbox`,
				setup: {
					name: `query`,
					theme: setup.theme,
					rules: {
						autocomplete: false, // try to avoid the browser suggesting values
						gracePeriod: false, // don't wait to start triggering change callbacks
						label: rules.label,
						icon: rules.icon,
						tooltip: rules.tooltip,
						errorMessage: rules.errorMessage,
						disabledMessage: rules.disabledMessage,
						prefix: rules.prefix,
						suffix: rules.suffix,
					},
					validation: { type: `String` },
					callbacks: {
						focus: (evt) => {
							const { query } = instances;
							const value = this.getValue();
							const option = this.find(rules.options, (option) => this.isEqual(value, option.value));
							const index = this.isObject(option) ? option.index : 0;

							data.open = true;
							this.addClass(`focus`);

							query.setValue(null);

							this.syncLabel();

							// TODO: suggestIndex() can't be called here (too early), but not setting the variable leaves unset in the interim (arrow key problems, etc.)
							this.show({
								target: listContainer,
								transition: `fade`,
								then: () => {
									const [target, fully] = [listContainer, true];
									const inViewport = DOM.inViewport({ fully, target });

									if (!inViewport) {
										this.addClass(`inverted`);
									}

									return this.defer({
										duration: 20,
										then: () => this.suggestIndex(index),
									});
								},
								done,
							});

							if (this.isFunction(this.callbacks.focus)) {
								this.callbacks.focus();
							}
						},
						blur: (evt) => {
							data.open = false;
							this.removeClass(`focus`);
							this.hide({
								target: listContainer,
								transition: `fade`,
								then: () => this.removeClass(`inverted`),
								done,
							});

							if (this.isInt(data.suggestedIndex)) {
								this.selectIndex(data.suggestedIndex);
							}

							this.syncLabel();

							if (this.isFunction(this.callbacks.blur)) {
								this.callbacks.blur();
							}
						},
						change: (value) => {
							const options = this.isArray(rules.options) ? rules.options : [];
							let matched = true;
							let count = 0;
							let index;

							if (this.isString(value, 1)) {
								value = value.toUpperCase();
							}

							this.chain(options)
								.clone()
								.sortBy((option) => {
									const { queryString } = option;
									let result = count;

									if (this.isString(value, 1)) {
										// a valid filter was provided
										const searchTerms = value.split(` `);
										const match = this.find(searchTerms, (term) => queryString.indexOf(term) >= 0);

										if (this.isString(match, 1)) {
											option.match = true;
											result = count - 1000000; // boost to the top
										} else {
											option.match = false;
										}
									} else {
										// no filter present
										option.match = true;
									}

									count++;

									return result;
								})
								.each((option, i) => {
									const element = this.elements.options[option.index];
									listContainer.appendChild(element);

									if (option.match) {
										this.removeClass(`divider`, element);
									} else {
										if (matched) {
											this.addClass(`divider`, element);
										} else {
											this.removeClass(`divider`, element);
										}

										matched = false;
									}

									if (i === 0) {
										index = option.index;
									}
								});

							this.suggestIndex(index);
						},
						submit: (value) => {
							this.blur();
						},
					},
				},
			});

			return this.when({
				promises: [rules.options, promise],
				spread: (options) => {
					if (this.isArray(options)) {
						this.populate(options);
					}
				},
			});
		},

		listeners: [
			{
				event: `mousedown`,
				filter: `li[index]`,
				handler(evt) {
					const link = evt.currentTarget;
					const index = parseInt(link.getAttribute(`index`), 10);

					this.selectIndex(index);
				},
			},
			{
				event: `keyup`,
				target: `input`,
				matchContainer: false,
				handler(evt) {
					const { data, elements } = this;
					let element;
					let sibling;
					let index;

					switch (evt.keyCode) {
						case 27: // ESC
							evt.preventDefault();
							evt.stopPropagation(); // avoid bubbling to close overlays, etc.
							data.suggestedIndex = null; // ignore the suggested value, keep the selected one
							this.blur();
							break;

						case 38: // Up arrow
						case 40: // Down arrow
							element = elements.options[data.suggestedIndex];
							if (this.isElement(element)) {
								// There is a currently selected element
								sibling = evt.keyCode === 38 ? element.previousElementSibling : element.nextElementSibling;
								if (this.isElement(sibling)) {
									index = parseInt(sibling.getAttribute(`index`), 10);
									this.suggestIndex(index);
								}
							}
							break;
					}
				},
			},
		],

		api: {
			populate: {
				value(options) {
					const { data, elements, instances } = this;
					const { listItemHasRadioButton } = data;
					const { listContainer } = elements;
					const elementList = [];
					let currentValue;
					let listData = {};

					currentValue = this.getValue(); // cache, to try re-setting after populating options

					if (this.isUndefined(currentValue)) {
						currentValue = null;
					}

					options = this.clone(options); // avoid the possibility of being manipulated externally
					this.assign(listData, {
						options,
						listItemHasRadioButton,
					});

					if (!data.validation.required) {
						options.unshift({
							label: ``,
							value: null,
						});
					}

					this.render({
						name: `list`,
						target: listContainer,
						data: listData,
					});

					this.each(options, (option, i) => {
						const element = this.get(`[index="${i}"]`, listContainer);
						const { alias, label, value } = option;
						let queryString = label;

						elementList.push(element);
						option.index = i; // cache for easier re-sorting later

						if (this.isString(value)) {
							queryString += ` ${value}`;
						}

						if (this.isString(alias)) {
							queryString += ` ${alias}`;
						}

						option.queryString = queryString.toUpperCase();
					});

					data.rules.options = options;
					elements.options = elementList;
					this.setValue(currentValue, true);
					instances.query.setValue(null, true);
				},
			},

			suggestIndex: {
				value(suggestedIndex) {
					const { App, data, elements } = this;
					const { listContainer: container } = elements;

					this.each(data.rules.options, ({ index }) => {
						const target = elements.options[index];

						if (suggestedIndex === index) {
							data.suggestedIndex = suggestedIndex;
							this.addClass(`suggested`, target);
							App.scrollTo({ container, target });
						} else {
							this.removeClass(`suggested`, target);
						}
					});
				},
			},

			selectIndex: {
				value(index) {
					const { options } = this.data.rules;
					const option = options[index];
					const value = this.getValue();

					if (value !== option.value) {
						this.setValue(option.value);
					}
				},
			},

			syncLabel: {
				value() {
					const { data, instances } = this;
					const { query: input } = instances;
					const { hideValueOnOpen, open, rules, useTranslatedLabel } = data;
					const value = this.getValue();

					if ((hideValueOnOpen && open) || this.isNull(value)) {
						const { label } = rules;
						input.updateRules({ label });
						input.setValue(null, true);
					} else if (!this.data.open || !hideValueOnOpen) {
						this.find(rules.options, (option) => {
							if (this.isEqual(option.value, value)) {
								if (useTranslatedLabel) {
									let selected = this.get(`ul > li.selected`);

									if (this.isElement(selected)) {
										input.setValue(selected.innerText, true);
									} else {
										input.setValue(option.label, true);
									}
								} else {
									input.setValue(option.label, true);
								}
								return true; // break loop
							}
						});
					}
				},
			},

			setValue: {
				public: true,
				value(context, returned, value, skipCallbacks) {
					const { data, elements } = this;
					const { rules, validation } = data;
					const { arrowIcon: target } = elements;
					const done = true;
					let found = false;

					if (this.isNull(returned)) {
						// not a valid value
						this.show({ done, target });
					} else {
						// a value is present
						if (validation.required) {
							// not allowed to cancel
							this.show({ done, target });
						} else {
							this.hide({ target, done });
						}
					}

					data.suggestedIndex = null;
					this.each(rules.options, (option) => {
						const element = elements.options[option.index];

						if (this.isEqual(option.value, returned)) {
							this.addClass(`selected`, element);
							data.suggestedIndex = option.index;
							found = true;
						} else {
							this.removeClass(`selected`, element);
						}
					});

					if (!found && !this.isNull(returned)) {
						this.setValue(null, true);
					}

					this.syncLabel();
				},
			},

			updateRules: {
				public: true,
				value(context, returned, updates) {
					const { instances, syncLabel } = this;
					const { query } = instances;
					const { icon, suffix, prefix } = updates;
					let promise;

					if (this.isString(icon)) {
						query.updateRules({ icon });
					}

					if (this.isString(suffix)) {
						query.updateRules({ suffix });
					}

					if (this.isString(prefix)) {
						query.updateRules({ prefix });
					}

					if (this.isDefined(updates.options)) {
						promise = this.populate(updates.options);
					}

					// in case a label update was passed
					this.when({ promise, then: syncLabel, done: true });
				},
			},

			updateValidation: {
				public: true,
				value(context, returned, updates) {
					const { validation } = this.data;
					const options = { done: true, selector: `[action="reset"]` };

					if (validation.required) {
						this.hide(options);
					} else {
						this.show(options);
					}
				},
			},

			disable: {
				public: true,
				value(context, returned) {
					this.instances.query.disable();
				},
			},

			enable: {
				public: true,
				value(context, returned) {
					this.instances.query.enable();
				},
			},

			focus: {
				public: true,
				value(context, returned) {
					this.instances.query.focus();
				},
			},

			blur: {
				public: true,
				value(context, returned) {
					this.instances.query.blur();
				},
			},

			onValidateError: {
				public: true,
				value(context, returned, reason) {
					this.instances.query.onValidateError(reason);
				},
			},

			onValidateSuccess: {
				public: true,
				value(context, returned) {
					this.instances.query.onValidateSuccess();
				},
			},
		},
	});
});

define("Inputs/Dropdown.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Inputs/Dropdown/list"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <li index=\""
    + container.escapeExpression(alias1((data && lookupProperty(data,"index")), depth0))
    + "\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"divider") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":40},"end":{"line":1,"column":78}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"notranslate") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":78},"end":{"line":1,"column":118}}})) != null ? stack1 : "")
    + "> <p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"label") : depth0), depth0)) != null ? stack1 : "")
    + "</p> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"tooltip") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":139},"end":{"line":1,"column":245}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"listItemHasRadioButton")),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":246},"end":{"line":1,"column":356}}})) != null ? stack1 : "")
    + " </li> ";
},"2":function(container,depth0,helpers,partials,data) {
    return " class=\"divider\"";
},"4":function(container,depth0,helpers,partials,data) {
    return "translate=\"no\"";
},"6":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <span class=\"info\" data-tooltip-text=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"tooltip") : depth0), depth0))
    + "\"> "
    + alias1(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"moreinfo",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":207},"end":{"line":1,"column":229}}}))
    + " </span> ";
},"8":function(container,depth0,helpers,partials,data) {
    return " <div class=\"radio-button\"> <div class=\"radio-inner\"></div> </div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"options") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":372}}})) != null ? stack1 : "");
},"useData":true});

this["Templates"]["Inputs/Dropdown/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"icon-container right\">  "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"arrow-down",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":96},"end":{"line":1,"column":120}}}))
    + " </div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"isIconAfterValue") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":139}}})) != null ? stack1 : "")
    + " <div name=\"query\"></div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"isIconAfterValue") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":165},"end":{"line":1,"column":296}}})) != null ? stack1 : "")
    + " <span class=\"icon-container right\" action=\"reset\">  "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(alias1,"close",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":378},"end":{"line":1,"column":397}}}))
    + " </span> <ul></ul> ";
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Dropdown.js.map